import clsx from "clsx";
import { type ButtonHTMLAttributes, forwardRef } from "react";

import { Spinner } from "./Spinner.tsx";
import "./Button.css";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | "primary"
    | "secondary"
    | "outline"
    | "neutral"
    | "dangerOutline"
    | "successOutline"
    | "ghost"
    | "danger"
    | "success"
    | "ocean"
    | "softOutline";
  size?: "sm" | "md" | "lg";
  loading?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
}

const variants = {
  primary: "ui-button-gradient ui-button-ocean",
  secondary: "ui-button-soft-outline",
  outline: "ui-button-soft-outline",
  neutral:
    "border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800",
  ghost: "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800",
  dangerOutline: "ui-button-soft-outline ui-button-danger-outline",
  successOutline: "ui-button-soft-outline ui-button-success-outline",
  danger: "ui-button-gradient ui-button-danger",
  success: "ui-button-gradient ui-button-success",
  ocean: "ui-button-gradient ui-button-ocean",
  softOutline: "ui-button-soft-outline",
};

const sizes = {
  sm: "px-3 py-2 text-sm",
  md: "px-4 py-2.5 text-sm",
  lg: "px-5 py-3 text-base",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      size = "md",
      loading,
      icon: Icon,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        className={clsx(
          "ui-button inline-flex items-center justify-center rounded-lg font-medium transition-[background-color,color,transform] duration-200 active:translate-y-px motion-reduce:transform-none motion-reduce:transition-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-slate-950",
          variants[variant],
          sizes[size],
          className,
        )}
        disabled={disabled || loading}
        data-ui-variant={variant}
        aria-busy={loading}
        {...props}
      >
        {loading && <Spinner size="sm" className="-ml-1 mr-2" />}
        {Icon && !loading && <Icon className="w-4 h-4 mr-2" />}
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";
